/**
 * 공통 API 클라이언트
 *
 * - JWT 토큰을 자동 부착 (localStorage 'authToken')
 * - 401 응답 시 'auth:unauthorized' 글로벌 이벤트 발행 (App.tsx에서 자동 로그아웃 트리거)
 * - 403 등 다른 에러는 ApiError로 throw (호출처에서 UI 처리)
 */

import { SERVER_BASE_URL } from '../utils/serverUrl';

export const API_BASE_URL = `${SERVER_BASE_URL}/api`;

export class ApiError extends Error {
  status: number;
  /** 서버 응답의 code(있으면) — 예: 기기 승인 DEVICE_LIMIT·CODE_MISMATCH */
  code?: string;
  /** 서버 응답 본문(오류 화면이 추가 필드를 읽을 때) */
  body?: Record<string, unknown>;
  constructor(message: string, status: number, code?: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

let unauthorizedDispatched = false;

/**
 * 401 → 전역 로그아웃 이벤트. detail.code는 서버가 준 이유(기기 신뢰: DEVICE_REVOKED·DEVICE_REQUIRED·
 * DEVICE_PROOF_REQUIRED)로, App이 문구를 고르고 DEVICE_REQUIRED면 기기 키 재개를 먼저 시도한다.
 */
export function dispatchUnauthorized(code?: string, message?: string): void {
  if (unauthorizedDispatched) return;
  unauthorizedDispatched = true;
  window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { code, message } }));
  setTimeout(() => {
    unauthorizedDispatched = false;
  }, 5000);
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('authToken');
  const isFormData = options.body instanceof FormData;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    const body = await res.json().catch(() => ({} as { code?: string; error?: string }));
    dispatchUnauthorized(body.code, body.error);
    throw new ApiError('세션이 만료되었습니다. 다시 로그인해주세요.', 401);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }));
    throw new ApiError(
      body.error || `HTTP ${res.status}`,
      res.status,
      typeof (body as { code?: unknown }).code === 'string' ? (body as { code: string }).code : undefined,
      body as Record<string, unknown>
    );
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
